import { classes } from "common/theme/theme";
import { FieldValidator, useField } from "formik";
import { FormLabel, Tooltip } from "@mui/material";
import { NumberInput } from "./NumberInput";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC, useMemo } from "react";

interface FormikNumberInputProps {
  label: string;
  name: string;
  validate?: boolean;
  validator?: FieldValidator;
  tooltipText?: string;
  // props passed through to localized error message
  tProps?: { [key: string]: number | string };
  numberInputProps?: Omit<
    React.ComponentProps<typeof NumberInput>,
    "label" | "value" | "onChange" | "error"
  >;
  className?: string;
}
/**
 * FormikNumberInput is a Formik-friendly wrapper to the <NumberInput> component,
 * which exhibits weird focus issues if you wrap it with <Field as={NumberInput} />.
 */
export const FormikNumberInput: FC<FormikNumberInputProps> = ({
  label,
  name,
  tooltipText,
  validator,
  validate = true,
  tProps,
  numberInputProps = {},
  className,
}) => {
  const [field, meta, helpers] = useField({ name, validate: validator });
  const { t } = useTranslation();
  const { value } = meta;
  const { setTouched, setValue } = helpers;
  const { onBlur } = field;
  const labelId = useMemo(() => uuidv4(), []);
  return (
    <div className={classes("flex flex-col w-full max-w-80", className)}>
      <FormLabel id={labelId} className="flex gap-2">
        {label}
        {tooltipText && (
          <Tooltip title={tooltipText} placement="top">
            <InfoOutlinedIcon className="flex my-auto" fontSize="small" />
          </Tooltip>
        )}
      </FormLabel>
      <NumberInput
        {...numberInputProps}
        label={""}
        value={value}
        onChange={(n) => {
          setTouched(true, validate);
          setValue(n);
        }}
        textFieldProps={{
          "aria-labelledby": labelId,
          ...numberInputProps.textFieldProps,
          onBlur,
        }}
        error={meta.error === undefined ? undefined : t(meta.error, tProps)}
      />
    </div>
  );
};
